import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { rfqApi } from '../../services/apiService';
import { useCurrency } from '../../context/CurrencyContext';
import './RequestInquireModal.css';

const RequestInquireModal = ({ 
  isOpen, 
  onClose, 
  onReply, 
  onUpgradePlan,
  onStatusChange,
  onSubmitQuotation,
  inquireData = {},
  isSubscribed = false, // Whether seller has premium subscription
  isLoading = false // Loading state for reply
}) => {
  const { formatPrice: currencyFormatPrice, selectedCurrency, currencies } = useCurrency();
  const [replyMessage, setReplyMessage] = useState('');
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteData, setQuoteData] = useState({ price: '', currency: selectedCurrency.code, description: '', deliveryTime: '', validUntil: '' });

  const {
    id,
    quantity,
    unit = 'Units',
    offerPrice,
    currency = selectedCurrency.code,
    email,
    phoneNumber,
    fullName,
    companyName,
    country,
    category,
    subCategory,
    details,
    status,
    createdAt
  } = inquireData;

  // Fetch replies when modal opens
  useEffect(() => {
    const fetchReplies = async () => {
      if (!isOpen || !id) return;
      
      try {
        setLoadingReplies(true);
        const data = await rfqApi.getReplies(id);
        setReplies(data.replies || []);
      } catch (error) {
        console.error('Error fetching RFQ replies:', error);
        setReplies([]);
      } finally {
        setLoadingReplies(false);
      }
    };
    
    fetchReplies();
  }, [isOpen, id]);

  // Mask sensitive data if not subscribed
  const maskEmail = (email) => {
    if (!email) return '**********************';
    if (isSubscribed) return email;
    const [name, domain] = email.split('@');
    return `${name.substring(0, 2)}${'*'.repeat(name.length - 2)}@${'*'.repeat(domain?.length || 5)}`;
  };

  const maskPhone = (phone) => {
    if (!phone) return '**********************';
    if (isSubscribed) return phone;
    return phone.substring(0, 4) + '*'.repeat(phone.length - 4);
  };

  const formatPrice = (price, curr) => {
    if (!price) return 'Not specified';
    return currencyFormatPrice(price);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleLater = () => {
    setShowReplyBox(false);
    setReplyMessage('');
    setShowQuoteForm(false);
    setQuoteData({ price: '', currency: selectedCurrency.code, description: '', deliveryTime: '', validUntil: '' });
    if (onClose) {
      onClose();
    }
  };

  const handleSubmitQuotation = async () => {
    if (!quoteData.price || !onSubmitQuotation) return;
    try {
      setQuoteSubmitting(true);
      await onSubmitQuotation(id, quoteData);
      setShowQuoteForm(false);
      setQuoteData({ price: '', currency: selectedCurrency.code, description: '', deliveryTime: '', validUntil: '' });
    } catch (err) {
      alert('Failed to submit quotation: ' + (err.response?.data?.error || err.message));
    } finally {
      setQuoteSubmitting(false);
    }
  };

  const handleReplyClick = () => {
    if (isSubscribed) {
      setShowReplyBox(true);
    } else {
      if (onUpgradePlan) {
        onUpgradePlan();
      }
    }
  };

  const handleSendReply = () => {
    if (onReply && replyMessage.trim() && !isLoading) {
      onReply({
        rfqId: id,
        message: replyMessage,
        buyerEmail: email,
        buyerName: fullName
      });
      // Don't close modal here - parent will close after success
      // and will handle status update
      setReplyMessage('');
      setShowReplyBox(false);
    }
  };

  const handleUpgradePlan = () => {
    if (onUpgradePlan) {
      onUpgradePlan();
    }
  };

  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'new': return 'status-new';
      case 'replied': return 'status-replied';
      case 'closed': return 'status-closed';
      default: return '';
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="request-inquire-modal-overlay" onClick={onClose}>
      <div className="request-inquire-modal" onClick={(e) => e.stopPropagation()}>
        <div className="request-inquire-content">
          <div className="request-inquire-header">
            <div className="request-inquire-title-row">
              <h2 className="request-inquire-title">Request for Inquire</h2>
              {status && (
                <span className={`request-inquire-status ${getStatusBadgeClass(status)}`}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
              )}
            </div>
            
            {category && (
              <p className="request-inquire-category">
                {category} {subCategory && `> ${subCategory}`}
              </p>
            )}
            
            {createdAt && (
              <p className="request-inquire-date">Submitted: {formatDate(createdAt)}</p>
            )}
            
            <div className="request-inquire-details-section">
              <div className="request-inquire-details-list">
                {quantity && (
                  <p className="request-inquire-detail-item">
                    <span className="request-inquire-detail-label">Quantity:</span>
                    <span className="request-inquire-detail-value"> {quantity} {unit}</span>
                  </p>
                )}
                
                <p className="request-inquire-detail-item">
                  <span className="request-inquire-detail-label">Offer price: </span>
                  <span className="request-inquire-detail-value">{formatPrice(offerPrice, currency)}</span>
                </p>
                
                <p className="request-inquire-detail-item">
                  <span className="request-inquire-detail-label">Email ID: </span>
                  <span className={`request-inquire-detail-value ${!isSubscribed ? 'masked' : ''}`}>
                    {maskEmail(email)}
                  </span>
                </p>

                <p className="request-inquire-detail-item">
                  <span className="request-inquire-detail-label">Phone: </span>
                  <span className={`request-inquire-detail-value ${!isSubscribed ? 'masked' : ''}`}>
                    {maskPhone(phoneNumber)}
                  </span>
                </p>

                {isSubscribed && (
                  <>
                    <p className="request-inquire-detail-item">
                      <span className="request-inquire-detail-label">Contact: </span>
                      <span className="request-inquire-detail-value">{fullName}</span>
                    </p>
                    
                    <p className="request-inquire-detail-item">
                      <span className="request-inquire-detail-label">Company: </span>
                      <span className="request-inquire-detail-value">{companyName}</span>
                    </p>
                    
                    <p className="request-inquire-detail-item">
                      <span className="request-inquire-detail-label">Country: </span>
                      <span className="request-inquire-detail-value">{country}</span>
                    </p>
                  </>
                )}
                
                <p className="request-inquire-detail-item details-text">
                  <span className="request-inquire-detail-label">Details: </span>
                  <span className={`request-inquire-detail-value ${!isSubscribed ? 'masked-details' : ''}`}>
                    {isSubscribed ? details : 'Upgrade your package to view full buyer details and send your quotation. Upgrade your package to view full buyer details and send your quotation.'}
                  </span>
                </p>
              </div>
              
              {!isSubscribed && (
                <p className="request-inquire-upgrade-message">
                  Upgrade your package to view full buyer details and send your quotation.
                </p>
              )}
            </div>
            
            {/* Replies/Conversations Section */}
            {replies.length > 0 && (
              <div className="request-inquire-replies-section">
                <h3 className="request-inquire-replies-title">
                  Supplier Responses ({replies.length})
                </h3>
                <div className="request-inquire-replies-list">
                  {replies.map((reply, index) => (
                    <div key={reply.conversationId || index} className="request-inquire-reply-item">
                      <div className="request-inquire-reply-header">
                        <div className="request-inquire-reply-supplier">
                          {reply.supplier?.avatar && (
                            <img 
                              src={reply.supplier.avatar} 
                              alt="" 
                              className="request-inquire-reply-avatar"
                            />
                          )}
                          <span className="request-inquire-reply-name">
                            {reply.supplier?.name || 'Supplier'}
                          </span>
                        </div>
                        <span className="request-inquire-reply-count">
                          {reply.messageCount} message{reply.messageCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="request-inquire-reply-messages">
                        {reply.messages.slice(-3).map((msg, msgIndex) => (
                          <div 
                            key={msg.id || msgIndex} 
                            className={`request-inquire-message ${msg.isSupplierMessage ? 'supplier-msg' : 'buyer-msg'}`}
                          >
                            <span className="request-inquire-message-sender">
                              {msg.isSupplierMessage ? reply.supplier?.name : 'You'}:
                            </span>
                            <span className="request-inquire-message-content">
                              {msg.content?.substring(0, 150)}{msg.content?.length > 150 ? '...' : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                      {reply.messageCount > 3 && (
                        <p className="request-inquire-view-all">
                          View all messages in Messages
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {loadingReplies && (
              <div className="request-inquire-loading-replies">
                Loading responses...
              </div>
            )}
            
            {!isSubscribed && (
              <button 
                className="request-inquire-upgrade-link"
                onClick={handleUpgradePlan}
              >
                Upgrade Plan
              </button>
            )}

            {showReplyBox && isSubscribed && (
              <div className="request-inquire-reply-section">
                <textarea
                  className="request-inquire-reply-textarea"
                  placeholder="Type your reply message here..."
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  rows={4}
                  disabled={isLoading}
                />
                <button 
                  className="request-inquire-send-btn"
                  onClick={handleSendReply}
                  disabled={!replyMessage.trim() || isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send Reply'}
                </button>
              </div>
            )}

            {/* Quotation Form */}
            {showQuoteForm && isSubscribed && onSubmitQuotation && (
              <div className="request-inquire-quote-section">
                <h3 className="request-inquire-quote-title">Submit Quotation</h3>
                <div className="request-inquire-quote-row">
                  <div className="request-inquire-quote-field">
                    <label>Price <span style={{color:'#ef4444'}}>*</span></label>
                    <div className="request-inquire-quote-price-row">
                      <select value={quoteData.currency} onChange={e => setQuoteData(prev => ({...prev, currency: e.target.value}))}>
                        {currencies.map(c => (
                          <option key={c.code} value={c.code}>{c.code}</option>
                        ))}
                      </select>
                      <input type="number" placeholder="0.00" value={quoteData.price} onChange={e => setQuoteData(prev => ({...prev, price: e.target.value}))} min="0" step="0.01" />
                    </div>
                  </div>
                  <div className="request-inquire-quote-field">
                    <label>Delivery Time</label>
                    <input type="text" placeholder="e.g. 2-3 weeks" value={quoteData.deliveryTime} onChange={e => setQuoteData(prev => ({...prev, deliveryTime: e.target.value}))} />
                  </div>
                </div>
                <div className="request-inquire-quote-field">
                  <label>Valid Until</label>
                  <input type="date" value={quoteData.validUntil} onChange={e => setQuoteData(prev => ({...prev, validUntil: e.target.value}))} min={new Date().toISOString().split('T')[0]} />
                </div>
                <div className="request-inquire-quote-field">
                  <label>Description / Notes</label>
                  <textarea placeholder="Additional details about your quotation..." value={quoteData.description} onChange={e => setQuoteData(prev => ({...prev, description: e.target.value}))} rows={3} />
                </div>
                <button className="request-inquire-send-btn" onClick={handleSubmitQuotation} disabled={!quoteData.price || quoteSubmitting}>
                  {quoteSubmitting ? 'Submitting...' : 'Submit Quotation'}
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="request-inquire-buttons">
          <button 
            className="request-inquire-later-btn"
            onClick={handleLater}
          >
            Later
          </button>
          {onSubmitQuotation && (
            <button 
              className="request-inquire-quote-btn"
              onClick={() => {
                if (isSubscribed) {
                  setShowQuoteForm(!showQuoteForm);
                  setShowReplyBox(false);
                } else {
                  onUpgradePlan && onUpgradePlan();
                }
              }}
            >
              {showQuoteForm ? 'Cancel Quote' : 'Quote'}
            </button>
          )}
          <button 
            className="request-inquire-reply-btn"
            onClick={handleReplyClick}
          >
            {showReplyBox ? 'Cancel' : 'Reply'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RequestInquireModal;
